import React, { useEffect, useRef, useState } from 'react';

import logoDark from '../../../../../resources/logo-dark.png';
import logoWhite from '../../../../../resources/logo-white.png';
import { ElementId } from '@ael/core-api';
import { AnimatedBackground } from '@ael/shared-components-library/student-shell';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';

import './loader.scss';
import './LoaderFadeoutTransition.scss';

type AnimatedLoaderProps = {
  isAppReady: boolean;
  branding: {
    logoUrl?: string | null | undefined;
    tagline?: string | null | undefined;
  };
  isLightModeEnabled: boolean;
};

export const AnimatedLoader = (props: AnimatedLoaderProps) => {
  const loaderRef = React.useRef<HTMLDivElement>(null);
  const [wasBrandingDisplayedLongEnough, setWasBrandingDisplayedLongEnough] = useState(false);
  const isLoaderHidden = wasBrandingDisplayedLongEnough && props.isAppReady;

  return (
    <CSSTransition nodeRef={loaderRef} in={!isLoaderHidden} timeout={700} unmountOnExit classNames="loader-fade-out">
      <Loader
        ref={loaderRef}
        logoUrl={props.branding.logoUrl}
        tagline={props.branding.tagline}
        onBrandWasDisplayedLongEnough={() => setWasBrandingDisplayedLongEnough(true)}
        isLightModeEnabled={props.isLightModeEnabled}
      />
    </CSSTransition>
  );
};

type LoaderProps = {
  onBrandWasDisplayedLongEnough?: () => void;
  logoUrl?: string | null;
  tagline?: string | null;
  isLightModeEnabled: boolean;
};

export const Loader = React.forwardRef<HTMLDivElement, LoaderProps>((props, ref) => {
  const [logoImageLoaded, setLogoImageLoaded] = useState(false);
  const callBackToParent = props.onBrandWasDisplayedLongEnough;

  useEffect(() => {
    if (logoImageLoaded) {
      const timeout = setTimeout(() => callBackToParent?.(), 2000);
      return () => clearTimeout(timeout);
    }
    return;
  }, [logoImageLoaded, callBackToParent]);

  return (
    <AnimatedBackground className="loader" data-elementid={ElementId.Loader} ref={ref}>
      <div />
      <div className="loader__body">
        <div className="loader__logo-wrapper">
          <LogoImage
            shouldDisplayLogoStart={logoImageLoaded}
            logoUrl={props.logoUrl}
            onLoad={() => setLogoImageLoaded(true)}
            isLightModeEnabled={props.isLightModeEnabled}
          />
        </div>
      </div>
      <div className="loader__footer">{logoImageLoaded && <LogoTagline tagline={props.tagline} />}</div>
    </AnimatedBackground>
  );
});

function LogoImage(props: {
  shouldDisplayLogoStart: boolean;
  logoUrl?: string | null;
  onLoad: () => void;
  isLightModeEnabled: boolean;
}) {
  const logoRef = useRef(null);

  return (
    <CSSTransition
      nodeRef={logoRef}
      in={props.shouldDisplayLogoStart}
      timeout={700}
      classNames="loader__logo--transition"
    >
      <img
        ref={logoRef}
        src={props.logoUrl ?? (props.isLightModeEnabled ? logoDark : logoWhite)}
        className={classNames(props.logoUrl ? 'loader__custom-logo' : 'loader__default-logo', {
          'loader__logo--not-loaded': !props.shouldDisplayLogoStart,
        })}
        alt="logo"
        onLoad={props.onLoad}
      />
    </CSSTransition>
  );
}
function LogoTagline(props: { tagline: string | null | undefined }) {
  const logoRef = useRef(null);
  // Both products (Panomio and V360E) ship the same student bundle, so the fallback is fixed.
  const defaultTagLine = 'Powered by Panomio';

  return (
    <CSSTransition nodeRef={logoRef} in appear timeout={700} classNames="loader__tagline--transition">
      <div className="text-text" ref={logoRef}>
        {props.tagline || defaultTagLine}
      </div>
    </CSSTransition>
  );
}
